import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useFetchCategories, useCreateCategory } from "@metadataHooks";
import { Tables } from "@/integrations/supabase/types";
import { CreatableSelect, CreatableSelectOption } from "@/components/creatable-select/CreatableSelect";

type SubCategory = Tables<"sub_categories">;

export interface CategorySubCategorySelectProps {
  categoryId: string;
  subCategoryId: string;
  onCategoryChange: (categoryId: string) => void;
  onSubCategoryChange: (subCategoryId: string) => void;
  disabled?: boolean;
}

function useSubCategories(categoryId: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["sub_categories", categoryId, user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sub_categories")
        .select("*")
        .eq("category_id", categoryId)
        .order("name");
      if (error) throw error;
      return data as SubCategory[];
    },
    enabled: !!user && !!categoryId,
  });
}

function useCreateSubCategory() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ name, categoryId }: { name: string; categoryId: string }) => {
      const { data, error } = await supabase
        .from("sub_categories")
        .insert({
          name,
          category_id: categoryId,
          user_id: user!.id,
          is_default: false,
        })
        .select()
        .single();
      if (error) throw error;
      return data as SubCategory;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["sub_categories", variables.categoryId] });
    },
  });
}

export function CategorySubCategorySelect({
  categoryId,
  subCategoryId,
  onCategoryChange,
  onSubCategoryChange,
  disabled = false,
}: CategorySubCategorySelectProps) {
  const { data: categories = [] } = useFetchCategories();
  const { data: subCategories = [] } = useSubCategories(categoryId);
  const createCategory = useCreateCategory();
  const createSubCategory = useCreateSubCategory();

  const categoryOptions: CreatableSelectOption[] = categories.map((c) => ({
    value: c.id,
    label: c.name,
    icon: c.icon ?? undefined,
  }));

  const subCategoryOptions: CreatableSelectOption[] = subCategories.map((sc) => ({
    value: sc.id,
    label: sc.name,
    icon: sc.icon ?? undefined,
  }));

  const handleCategoryChange = (newCategoryId: string) => {
    onSubCategoryChange("");
    onCategoryChange(newCategoryId);
  };

  const handleCreateCategory = async (name: string): Promise<string> => {
    const result = await createCategory.mutateAsync({ name, type: "expense" });
    return result.id;
  };

  const handleCreateSubCategory = async (name: string): Promise<string> => {
    const result = await createSubCategory.mutateAsync({ name, categoryId });
    return result.id;
  };

  return (
    <div className="flex flex-col gap-3">
      <CreatableSelect
        value={categoryId}
        onValueChange={handleCategoryChange}
        options={categoryOptions}
        placeholder="Select or create a category..."
        onCreate={handleCreateCategory}
        disabled={disabled}
      />
      <CreatableSelect
        value={subCategoryId}
        onValueChange={onSubCategoryChange}
        options={subCategoryOptions}
        placeholder="Select or create a sub-category..."
        onCreate={handleCreateSubCategory}
        disabled={disabled || !categoryId}
      />
    </div>
  );
}
